import jwt_decode from 'jwt-decode';
import Vue from 'vue';
import auth from '../../../api/auth';
import roles from '../../acl/AclRoles.js';
import industryType from '../../constants-v2/crm/industry-type';
import companyType from '../../constants-v2/person/company-type.js';
import disabilityLevel from '../../constants-v2/person/disability-level.js';
import { USER_TYPE_CRM, USER_TYPE_STAFF } from '../../constants-v2/user';
import { DEFAULT_FREE_TAX_AMOUNT_VALUE } from '../../constants/company.js';

const getBasePersonAddress = () => {
  return {
    residence_address: {},
    registered_address: {},
    correspondence_address: {},
    company_address: {},
  };
};
const getBaseCompany = () => {
  return {
    name: '',
    surname: '',
    nip: '',
    nip_ue: '',
    regon: '',
    phone: '',
    email: '',
    address: {},
    pkd: '',
    proportion: '',
    tax_loss_value: 0,
    business_activity_start: '',
    suspension_of_business_activity_from: '',
    suspension_of_business_activity_to: '',
    krs: '',
    register_of_entrepreneurs: '',
    companytype: companyType.JDG,
    tax_office: {},
    official_bank_account: '',
    form_of_accounting: '',
    pit_settlement: '',
    tax_type: '',
    vat_payer: '',
    vat_settlement: '',
    settlement_method: '',
    resignation_of_vat_r: '',
    resignation_from_dismissal: '',
    intracommunitary_transactions: '',
    cash_register: '',
    include_free_tax_amount: true,
    free_tax_amount_value: DEFAULT_FREE_TAX_AMOUNT_VALUE,
    income_previous_year: '',
    basis_for_health_contribution_base: false,
    start_reduction_from: '',
    start_reduction_to: '',
    agrees_to_the_disease_from: '',
    using_preference_zus_from: '',
    using_preference_zus_to: '',
    own_company_from: '',
    own_company_to: '',
    has_maly_zus_full_fp: false,
    right_to_pension: '',
    disability_level: disabilityLevel.NONE_ID,
    using_maly_zus: '',
    preference_zus_civillaw_income: '',
    have_income_from_civillaw_contract: '',
    is_employment_contract: '',
    is_foreign_company: false,
    is_affiliated_entity: false,
    country_code: '',
    vat_exemption_basis: null,
    zus_account_number: '',
    partner: [],
    right_to_retirement: '',
  };
};
const getBasePerson = () => {
  return {
    name: '',
    second_name: '',
    surname: '',
    family_name: '',
    phone: '',
    email: '',
    correspondence_email: '',
    citizenship: '',
    pesel: '',
    birth_date: null,
    gender: null,
    personaddress: getBasePersonAddress(),
    company: getBaseCompany(),
    bankaccounts: [],
    personbankaccount: {
      main_bank_account: {},
    },
    identification_type: '',
    identification_series: '',
    identification_number: '',
    identification_issue_place: '',
    identification_issue_by: '',
    identification_issue_date: '',
    identification_expiration_date: '',
  };
};

const getBaseState = (payload = {}) => ({
  person: getBasePerson(),
  scopes: [],
  acls: [],
  industrytype: {},
  citizenships: [
    { value: 1, label: 'Polskie' },
    { value: 2, label: 'Czeskie' },
  ],
  loggedIn: false,
  demoStatus: undefined,
  user: {},
  boss: {},
  mainCompany: {},
  accountTabs: [],
  refreshAccountTabs: false,
  activeAccountData: {},
  activeAccountParent: undefined,
  modulesToUpdate: {},
  dictionary: {
    countries: [],
  },
  ...payload,
});

const state = getBaseState({
  loggedIn: auth.loggedIn(),
});

const prepareActiveCompany = (state) => {
  //check if switched to different crm
  if (state.activeAccountData.crmId) {
    return state.activeAccountData.personData ? state.activeAccountData.personData.company : false;
  }

  let bossCompany = _.get(state.boss, 'company');

  if (bossCompany) {
    return bossCompany;
  }

  return state.person.company;
};

const prepareActiveBoss = (state) => {
  //check if switched to different crm
  if (state.activeAccountData?.crmId) {
    return state.activeAccountData.personData ? state.activeAccountData.personData : false;
  }

  if (!_.isEmpty(state.boss)) {
    return state.boss;
  }

  return state.person;
};

const getters = {
  isImpersonated(state) {
    return state.user.parent !== state?.activeAccountParent;
  },
  isIndustryTypeAo(state, getters) {
    return getters.hasIndustryType(industryType.ao);
  },
  isIndustryTypeAoc(state, getters) {
    return getters.hasIndustryType(industryType.aoc);
  },
  isIndustryTypeBroker(state, getters) {
    return getters.hasIndustryType(industryType.broker);
  },
  hasIndustryType(state) {
    return type => state.scopes?.includes(`industry:${ type }`);
  },
  currentUser(state) {
    return state.user;
  },
  isLoggedIn(state) {
    return state.loggedIn && state.user;
  },
  isUserEmployee(state) {
    return state?.user?.type === USER_TYPE_STAFF;
  },
  isUserCrm(state) {
    return state?.user?.type === USER_TYPE_CRM;
  },
  hasScope: (state) => (scope) => state.scopes.includes(scope),
  aclHave(state) {
    return (acl) => state.acls.includes(acl);
  },
  getCorrespondenceAddress: state => {
    return state.person.personaddress.correspondence_address;
  },
  getRegisteredAddress: state => {
    return state.person.personaddress.registered_address;
  },
  getResidenceAddress: state => {
    return state.person.personaddress.residence_address;
  },
  getCompanyAddress: state => {
    return state.person.personaddress.company_address;
  },
  getPersonData: state => state?.activeAccountData?.personData || {},
  getPersonId: state => {
    return state.person.id;
  },
  getPersonOriginal: state => {
    return state.person.original;
  },
  getActivePerson: state => {
    if (getters.isImpersonated(state)) {
      return getters.getPersonData(state);
    }

    return state.person;
  },
  activeAccount: state => state?.activeAccountData,
  getBankAccounts: state => {
    return state.person.bankaccounts;
  },
  getDefaultAccount: state => {
    if (state?.person?.personbankaccount?.main_bank_account?.id) {
      return state.person.personbankaccount.main_bank_account;
    }
    return {};
  },
  getActiveBoss: state => {
    return prepareActiveBoss(state);
  },
  getActiveBossCompany: state => {
    return prepareActiveCompany(state);
  },
  getActiveBossCompanyFormOfAccounting: (state) => {
    let activeCompany = prepareActiveCompany(state);
    if (!activeCompany) {
      return false;
    }

    if (null === activeCompany.form_of_accounting || undefined === activeCompany.form_of_accounting) {
      return false;
    }

    return activeCompany.form_of_accounting;
  },
  getBossCompanyAddress: state => {
    if (!state.boss) {
      return {};
    }
    if (!state.boss.personaddress) {
      return {};
    }
    if (!state.boss.personaddress.company_address) {
      return {};
    }
    if (!state.boss.personaddress.company_address.id) {
      return {};
    }
    return state.boss.personaddress.company_address;
  },
  getMainAccountAccessToken: state => {
    return state.user.accessToken;
  },
  getMainAccountServiceParent: state => {
    return state.user.serviceparent;
  },
  getCrmModuleToUpdate: state => state.modulesToUpdate['crm'],
};

const actions = {
  updatePerson({ commit, state }) {
    return new Promise((resolve, reject) => {
      for (let i in state.modulesToUpdate) {
        if (i === 'staff') {
          commit('prepareIndustryType', i);
          this.dispatch('STAFF_MODULE/updateStaff', state.modulesToUpdate[i]).then((response) => {
            commit('setPerson', response.data.employee.person);
            resolve(response);
          }, error => {
            reject(error);
          });
        }
        if (i === 'crm') {
          commit('prepareIndustryType', i);
          this.dispatch('CRM_MODULE/updateCrm', state.modulesToUpdate[i]).then((response) => {
            commit('setPerson', response.data.client.person);
            resolve(response);
          }, error => {
            reject(error);
          });
        }
      }
    });
  },
  showPerson({ commit, state }, id) {
    commit('LOADER/setLoading', true, { root: true });
    return new Promise((resolve, reject) => {
      auth.PERSON.show(id).then(response => {
        commit('setPerson', response.data);
        resolve(response);
      }).catch(error => {
        reject(error);
      }).finally(function () {
        commit('LOADER/setLoading', false, { root: true });
      });
    });
  },
  updatePassword({}, { passwordData, id }) {
    return new Promise((resolve, reject) => {
      auth.USER.updatePassword(id, passwordData).then(response => {
        resolve(response);
      }).catch(error => {
        reject(error);
      });
    });
  },
  showProfile({ commit, state }, id) {
    commit('LOADER/setLoading', true, { root: true });
    return new Promise((resolve, reject) => {
      auth.PROFILE.show(id).then(response => {
        let person = getPersonFromData(response.data);
        commit('setModulesToUpdate', {});
        if (person) {
          commit('setModulesToUpdate', person.modules);
          commit('setPerson', person.data);
          commit('setIndustryType', person.industrytype);

          if (person.data?.email == null || person.data?.email?.length == 0) {
            commit('setEmail', localStorage.getItem('login'));
          }

          resolve(response);
        }
      }).catch(error => {
        reject(error);
      }).finally(function () {
        commit('LOADER/setLoading', false, { root: true });
      });
    });
  },
  showUser({ commit, state }, id) {
    commit('LOADER/setLoading', true, { root: true });
    return new Promise((resolve, reject) => {
      auth.USER.show(id).then(response => {
        resolve(response);
      }).catch(error => {
        reject(error);
      }).finally(function () {
        commit('LOADER/setLoading', false, { root: true });
      });
    });
  },
  showBoss({ commit }) {
    commit('LOADER/setLoading', true, { root: true });
    return new Promise((resolve, reject) => {
      auth.BOSS.show().then(response => {
        commit('setBoss', response.data);
        resolve(response);
      }).catch(error => {
        reject(error);
      }).finally(function () {
        commit('LOADER/setLoading', false, { root: true });
      });
    });
  },
  register({ commit, dispatch }, userData) {
    return new Promise((resolve, reject) => {
      auth.registerUser(userData).then(response => {
        dispatch('prepareAuth', response.data);
        resolve(response);
      }).catch(error => {
        reject(error);
      });
    });
  },
  login({ commit, dispatch }, userData) {
    return auth.loginUser(userData)
      .then(response => {
        commit('setActiveAccountParent', response.data.parent);
        dispatch('prepareAuth', response.data);
        dispatch('FILTERS/clear', {}, { root: true });

        return response;
      });
  },
  // Re-bootstrap the whole auth state from the server using the current token — same effect as
  // login, without credentials. Used by the /bootstrap splash to pick up a re-versioned person
  // (and scopes) after onboarding finish.
  refreshSession({ commit, dispatch }) {
    return auth.accessTokens()
      .then(response => {
        commit('setActiveAccountParent', response.data.parent);
        dispatch('prepareAuth', response.data);
        dispatch('FILTERS/clear', {}, { root: true });

        return response;
      });
  },
  resetPassword({ commit, dispatch }, userData) {
    return auth.resetPasswordUser(userData);
  },
  setLocalStorageUser({ commit }, user) {
    commit('setLocalStorageUser', user);
  },
  clearPerson({ commit }) {
    commit('clearPerson');
  },
  logout({ commit }) {
    commit('setLogout');
    commit('clearMainCompany');
    commit('clearActiveAccountData');
    commit('setScopes');
  },
  updateResidenceAddress({ commit }, residence_address) {
    commit('LOADER/setLoading', true, { root: true });
    return new Promise((resolve) => {
      commit('setResidenceAddress', residence_address);
      setTimeout(function () {
        commit('LOADER/setLoading', false, { root: true });
        resolve();
      }, 1000);
    });
  },
  updateRegisteredAddress({ commit }, registered_address) {
    commit('LOADER/setLoading', true, { root: true });
    commit('setRegisteredAddress', registered_address);
    setTimeout(function () {
      commit('LOADER/setLoading', false, { root: true });
    }, 1000);
  },
  updateCorrespondenceAddress({ commit }, correspondence_address) {
    commit('LOADER/setLoading', true, { root: true });
    commit('setCorrespondenceAddress', correspondence_address);
    setTimeout(function () {
      commit('LOADER/setLoading', false, { root: true });
    }, 1000);
  },
  clearAddress({ commit }) {
    commit('clearAddress');
  },
  switchAccount({ commit, dispatch }, payload) {
    return new Promise((resolve) => {
      commit('switchAccount', payload);
      commit('setScopes', payload.accessToken);
      commit('addAccountTab', payload);
      resolve();
    });
  },

  async switchAccountByParent({ commit, dispatch, state }, { parent }) {
    if (state.activeAccountParent === parent) {
      return;
    }

    if (localStorage.getItem('main') === parent) {
      return await dispatch('switchToMainAccount');
    }

    const accountPayload = state.accountTabs.find(item => {
      return item.parent === parent;
    });

    if (accountPayload) {
      return await dispatch('switchAccount', accountPayload);
    }

    return await dispatch('switchToMainAccount');
  },

  switchToMainAccount({ commit, state }) {
    return new Promise((resolve) => {
      commit('switchAccount', { ...state.mainCompany });
      commit('setScopes', state.mainCompany.accessToken);
      resolve();
    });
  },
  updateActiveAccountData({ commit, dispatch }, payload) {
    return new Promise((resolve) => {
      commit('setActiveAccountData', payload);
      resolve();
    });
  },
  getCountryDictionary({ commit }) {
    return new Promise((resolve, reject) => {
      auth.getCountryDictionary().then(response => {
        localStorage.setItem('dictionaryCountries', JSON.stringify(response.data));
        commit('setCountryDictionary', JSON.parse(localStorage.getItem('dictionaryCountries')));
        resolve(response);
      }).catch(error => {
        reject(error);
      });
    });
  },
  setCountryDictionary({ commit }, payload) {
    commit('setCountryDictionary', payload);
  },
  prepareAuth({ commit }, data) {
    commit('clearAccountTabs');
    commit('clearActiveAccountData');
    commit('setPerson', data.person);
    commit('setIndustryType', data.person.industrytype);
    commit('setAuth', data);
    commit('setScopes', data.accessToken);
  },
  updateAccessToken({ commit }, payload) {
    commit('updateAccessToken', payload);
    commit('setScopes', payload.accessToken);
  },
};

const mutations = {
  updateCompany(state, { key, value }) {
    // Vue.set: firma z localStorage może nie mieć nowego klucza, a zwykłe przypisanie nie byłoby reaktywne.
    Vue.set(state.person.company, key, value);
  },
  updatePerson(state, { key, value }) {
    state.person[key] = value;
  },
  setPerson(state, person) {
    if (person.personaddress == null) {
      person.personaddress = getBasePersonAddress();
    }
    if (person.company == null) {
      person.company = getBaseCompany();
    }
    state.person = person;
    auth.setToken('person', JSON.stringify(person));
  },
  clearPerson(state) {
    state.person = getBasePerson();
  },
  setSecondName(state, secondName) {
    state.person.second_name = secondName;
  },
  setSurname(state, surname) {
    state.person.surname = surname;
  },
  setFamilyName(state, familyName) {
    state.person.family_name = familyName;
  },
  setPhone(state, phone) {
    state.person.phone = phone;
  },
  setEmail(state, email) {
    state.person.email = email;
  },
  setCorrespondenceEmail(state, correspondence_email) {
    state.person.correspondence_email = correspondence_email;
  },
  setCitizenship(state, citizenship) {
    state.person.citizenship = citizenship;
  },
  setPesel(state, pesel) {
    state.person.pesel = pesel;
  },
  setBirthDate(state, birthDate) {
    state.person.birth_date = birthDate;
  },
  setGender(state, gender) {
    state.person.gender = gender;
  },
  setDocumentType(state, type) {
    state.document.document_type = type;
  },
  setDocumentNumber(state, number) {
    state.document.document_number = number;
  },
  setReleaseDate(state, date) {
    state.document.release_date = date;
  },
  setExpirationDate(state, date) {
    state.document.expiration_date = date;
  },
  clearDocument(state) {
    state.document = { ...baseDocument };
  },
  setResidenceAddress(state, residence_address) {
    state.person.personaddress.residence_address = residence_address;
  },
  setRegisteredAddress(state, registered_address) {
    state.person.personaddress.registered_address = registered_address;
  },
  setCorrespondenceAddress(state, correspondence_address) {
    state.person.personaddress.correspondence_address = correspondence_address;
  },
  setCompanyAddress(state, company_address) {
    state.person.personaddress.company_address = company_address;
  },
  setBoss(state, boss) {
    state.boss = boss;
  },
  setAuth(state, user) {
    state.user = user;
    state.loggedIn = true;
    state.demoStatus = user.demoStatus;

    auth.setToken('token', state.user.accessToken);
    auth.setToken('person', JSON.stringify(state.user.person));
    auth.setToken('parent', state.user.parent);
    auth.setToken('serviceparent', state.user.serviceparent);
    auth.setToken('main', state.user.parent);
    auth.setToken('userId', state.user.id);
    auth.setToken('login', state.user.login);
  },
  switchAccount(state, payload) {
    auth.setToken('parent', payload.parent);
    auth.setToken('serviceparent', payload.serviceparent);
    auth.setToken('token', payload.accessToken);

    state.activeAccountParent = payload.parent;
    state.activeAccountData = {
      company: payload.company,
      person: payload.person,
      crmId: payload.crmId,
      crmOriginal: payload.crmOriginal,
      personData: payload.personData,
    };
  },
  setScopes(state, accessToken) {
    if (!accessToken) {
      state.scopes = [];
      state.acls = [];
      return;
    }

    try {
      const { scopes } = jwt_decode(accessToken);
      state.scopes = scopes;
      state.acls = _.union(...scopes.map((scope) => roles?.[scope] || []));
    } catch (e) {
      state.scopes = [];
      state.acls = [];
      console.error(e);
    }
  },
  clearActiveAccountData(state) {
    state.activeAccountData = {};
  },
  refreshAccountTabs(state) {
    state.refreshAccountTabs = !state.refreshAccountTabs;
  },
  setActiveAccountData(state, payload) {
    state.activeAccountData = {
      ...state.activeAccountData,
      ...payload,
    };

    state.accountTabs.forEach((element, index) => {
      if (element.parent === payload.parent) {
        state.accountTabs[index] = {
          ...state.accountTabs[index],
          ...payload,
        };
      }
    });
  },
  setActiveAccountParent(state, parent) {
    state.activeAccountParent = parent;
  },
  addAccountTab(state, payload) {
    if (state.mainCompany.parent === payload.parent) {
      return;
    }

    const account = state.accountTabs.find((el) => el.parent === payload.parent);

    if (account === undefined) {
      state.accountTabs.push({
        parent: payload.parent,
        serviceparent: payload.serviceparent,
        person: payload.person,
        personData: payload.personData,
        company: payload.company,
        companyNip: payload.companyNip,
        crmId: payload.crmId,
        crmOriginal: payload.crmOriginal,
        accessToken: payload.accessToken,
      });
    } else {
      state.refreshAccountTabs = !state.refreshAccountTabs;
    }
  },
  updateAccessToken(state, { parent, accessToken }) {
    auth.setToken('token', accessToken);

    state.accountTabs = state.accountTabs.map((tab) => {
      if (tab.parent !== parent) {
        return tab;
      }

      return { ...tab, accessToken };
    });

    if (state.mainCompany.parent === parent) {
      state.mainCompany = { ...state.mainCompany, accessToken };
    }

    if (state.user.parent === parent) {
      state.user = { ...state.user, accessToken };
    }
  },
  removeAccountTab(state, index) {
    state.accountTabs = state.accountTabs.filter(function (obj, key) {
      return key !== index;
    });
  },
  setLocalStorageUser(state, user) {
    localStorage.removeItem('person');
    auth.setToken('person', JSON.stringify(user));
  },
  setLogout(state) {
    let baseState = getBaseState();
    Object.entries(baseState).forEach(([key, item]) => {
      state[key] = item;
    });
    localStorage.removeItem('dictionary');
    localStorage.removeItem('dictionaryCountries');
    auth.destroyToken();
  },
  clearAccountTabs(state) {
    state.accountTabs = [];
  },
  setMainCompany(state, payload) {
    state.mainCompany = payload;
  },
  clearMainCompany(state) {
    state.mainCompany = {};
  },
  setCompanyPhone(state, phone) {
    state.person.company.phone = phone;
  },
  setCompanyEmail(state, email) {
    state.person.company.email = email;
  },
  setCompanyName(state, name) {
    state.person.company.name = name;
  },
  setNip(state, nip) {
    state.person.company.nip = nip;
  },
  setNipUE(state, nip_ue) {
    state.person.company.nip_ue = nip_ue;
  },
  setIsForeignCompany(state, is_foreign_company) {
    state.person.company.is_foreign_company = is_foreign_company;
  },
  setIsAffiliatedEntity(state, is_affiliated_entity) {
    state.person.company.is_affiliated_entity = is_affiliated_entity;
  },
  setCountryCode(state, country_code) {
    state.person.company.country_code = country_code.toUpperCase();
  },
  setVatExemptionBasis(state, value) {
    state.person.company.vat_exemption_basis = value;
  },
  setRegon(state, regon) {
    state.person.company.regon = regon;
  },
  setCompanyPkd(state, pkd) {
    state.person.company.pkd = pkd;
  },
  setCompanyProportion(state, proportion) {
    state.person.company.proportion = proportion;
  },
  setCompanyTaxLossValue(state, tax_loss_value) {
    state.person.company.tax_loss_value = tax_loss_value;
  },
  setCompanyBusinessActivityStart(state, business_activity_start) {
    state.person.company.business_activity_start = business_activity_start;
  },
  setCompanySuspensionOfBusinessActivityFrom(state, suspension_of_business_activity_from) {
    state.person.company.suspension_of_business_activity_from = suspension_of_business_activity_from;
  },
  setCompanySuspensionOfBusinessActivityTo(state, suspension_of_business_activity_to) {
    state.person.company.suspension_of_business_activity_to = suspension_of_business_activity_to;
  },
  setCompanyKrs(state, krs) {
    state.person.company.krs = krs;
  },
  setCompanyRegisterOfEntreoreneurs(state, register_of_entrepreneurs) {
    state.person.company.register_of_entrepreneurs = register_of_entrepreneurs;
  },
  setCompanyType(state, companytype) {
    state.person.company.companytype = companytype;
  },
  setCompanyOfficialBankAccount(state, official_bank_account) {
    state.person.company.official_bank_account = official_bank_account;
  },
  setCompanyTaxOffice(state, tax_office) {
    state.person.company.tax_office = tax_office;
  },
  setCompanyFormOfAccounting(state, form_of_accounting) {
    state.person.company.form_of_accounting = form_of_accounting;
  },
  setCompanyPitSettlement(state, pit_settlement) {
    state.person.company.pit_settlement = pit_settlement;
  },
  setCompanyTaxType(state, tax_type) {
    state.person.company.tax_type = tax_type;
  },
  setCompanyVatPayer(state, vat_payer) {
    state.person.company.vat_payer = vat_payer;
  },
  setCompanyVatSettlement(state, vat_settlement) {
    state.person.company.vat_settlement = vat_settlement;
  },
  setCompanySettlementMethod(state, settlement_method) {
    state.person.company.settlement_method = settlement_method;
  },
  setCompanyResignationFromDismissal(state, resignation_from_dismissal) {
    state.person.company.resignation_from_dismissal = resignation_from_dismissal;
  },
  setCompanyStartReductionFrom(state, start_reduction_from) {
    state.person.company.start_reduction_from = start_reduction_from;
  },
  setCompanyStartReductionTo(state, start_reduction_to) {
    state.person.company.start_reduction_to = start_reduction_to;
  },
  setCompanyAgreesToTheDiseaseFrom(state, agrees_to_the_disease_from) {
    state.person.company.agrees_to_the_disease_from = agrees_to_the_disease_from;
  },
  setCompanyUsingPreferenceZusFrom(state, using_preference_zus_from) {
    state.person.company.using_preference_zus_from = using_preference_zus_from;
  },
  setCompanyUsingPreferenceZusTo(state, using_preference_zus_to) {
    state.person.company.using_preference_zus_to = using_preference_zus_to;
  },
  setCompanyDisabilityLevel(state, disability_level) {
    state.person.company.disability_level = disability_level;
  },
  setCompanyUsingMalyZus(state, using_maly_zus) {
    state.person.company.using_maly_zus = using_maly_zus;
  },
  setCompanyLogoUrl(state, logo) {

    let logos = state.person.company.companylogo;
    let invoiceLogo = logos.find(({ module }) => module === 'invoice');
    if (invoiceLogo === undefined) {
      logos.push({ module: 'invoice', _logo_url: logo });
    } else {
      invoiceLogo._logo_url = logo;
    }
  },
  setCompanyLogoFilename(state, logo) {
    let logos = state.person.company.companylogo;
    let invoiceLogo = logos.find(({ module }) => module === 'invoice');
    if (invoiceLogo === undefined) {
      logos.push({ module: 'invoice', logo_filename: logo });
    } else {
      if (logo === null) {
        logos.splice(logos.findIndex(item => item.module === 'invoice'), 1);
      } else {
        invoiceLogo.logo_filename = logo;
      }
    }
  },
  setCompanyLogoSrc(state, logo) {
    let logos = state.person.company.companylogo;
    let invoiceLogo = logos.find(({ module }) => module === 'invoice');
    if (invoiceLogo === undefined) {
      logos.push({ module: 'invoice', _logo_src: logo });
    } else {
      invoiceLogo._logo_src = logo;
    }
  },
  setModulesToUpdate(state, modules) {
    state.modulesToUpdate = modules;
  },
  setCrmModuleToUpdate(state, crm) {
    state.modulesToUpdate['crm'] = crm;
  },
  addCompanyBankAccount(state, account) {
    state.person.bankaccounts.push(account);
  },
  setDefaultAccount(state, account) {
    if (!state.person.personbankaccount) {
      state.person.personbankaccount = {
        id: 0,
      };
    }
    state.person.personbankaccount.main_bank_account = account;
  },
  setCountryDictionary(state, dictionary) {
    let tmpArray = [];
    dictionary.forEach((item) => {
      let tmpItem = {
        ...item,
        label: item.citizenship.charAt(0).toUpperCase() + item.citizenship.slice(1),
        value: item.id,
        key: item.country_code,
      };
      tmpArray.push(tmpItem);
    });
    state.dictionary.countries = tmpArray;
  },
  setIndustryType(state, industry_type) {
    state.industrytype = industry_type;
  },
  prepareIndustryType(state, type) {
    state.modulesToUpdate[type].industrytype = state.industrytype;
  },
};

function getPersonFromData(data) {
  let modules = {};
  if (data.staff && Object.entries(data.staff).length > 0) {
    modules.staff = data.staff;
    return {
      modules,
      data: data.staff.employee.person,
      industrytype: data.staff.industrytype,
    };
  }
  if (data.crm && Object.entries(data.crm).length > 0) {
    modules.crm = data.crm;
    return {
      modules,
      data: data.crm.client.person,
      industrytype: data.crm.industrytype,
    };
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
